import React, { useState } from 'react'
import { Button, List, Modal, Form, Input } from 'antd'
import { observer } from 'mobx-react-lite'
import ProgressInput from './ProgressInput'
import { LstTypeBase } from '../..//cls/base/cls-lst-type-base'
import { v4 as uuidv4 } from 'uuid'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'

import { useStores } from '../../cls/stores/MobXProvider'
import { Link } from 'react-router-dom'
import FormItem from './FormItem'
import { useSmartTranslation } from '../../cls/TranslationContext'
const { TextArea } = Input
/**
 * EditItem component renders a modal form for creating or editing a list item.
 * It supports fields for status (optional), name, description, and any additional edit section.
 * The form can be disabled for view-only mode or based on user permissions.
 *
 * @component
 * @param {Object} props.item – The item to edit; if undefined, a new item is created.
 * @param {Function} props.onSaveItem – Callback invoked with form values on submit.
 * @param {Function} props.onCancel – Callback to close the modal without saving.
 * @param {Function} [props.additionalEditSection] – Optional function to render extra form fields.
 * @param {boolean} [props.phaseTwoLimited] – If true, restricts status choices in ProgressInput.
 * @param {boolean} [props.noProgress] – If true, hides the status field entirely.
 * @param {boolean} [props.viewOnly] – If true, form is rendered in read‐only mode.
 * @returns {JSX.Element}
 */
const EditItem = observer(
	({
		item,
		onSaveItem,
		onCancel,
		additionalEditSection,
		phaseTwoLimited,
		noProgress,
		viewOnly
	}) => {
		const { users } = useStores()
		const { loggedInUser } = users
		const isAdmin = loggedInUser.isAdmin
		const { t } = useSmartTranslation()
		const [isModalVisible, setIsModalVisible] = useState(true)

		// if item.id does not exist, it is a new item and we need to generate a uuid
		const editingItem = item || {}
		if (!item) {
			editingItem.id = uuidv4()
			editingItem.status = noProgress ? 'active' : 'draft'
		}

		const [form] = Form.useForm()

		const status = Form.useWatch('status', form)

		const isDisabled = viewOnly
			? true
			: !isAdmin
			? !(status === 'draft')
			: false

		return (
			<Modal
				title={item ? t('__edit%_item') : t('__add%_item.single')}
				open={isModalVisible}
				onCancel={() => {
					setIsModalVisible(false)
					onCancel() // Ensure modal close state is updated in parent
				}}
				width="100%"
				footer={null}
			>
				<Form
					labelCol={{ span: 4 }}
					wrapperCol={{ span: 20 }}
					initialValues={editingItem}
					onFinish={onSaveItem}
					form={form}
					disabled={isDisabled}
					className={viewOnly && 'view-only-styles'}
				>
					<FormItem name="id" hidden>
						<Input type="hidden" />
					</FormItem>

					<FormItem
						label={t('single.status')}
						name="status"
						hidden={noProgress}
						rules={[
							{
								required: true,
								message: t('__select.please%single.status')
							}
						]}
					>
						<ProgressInput phaseTwoLimited={phaseTwoLimited} />
					</FormItem>

					<FormItem
						label={t('single.name')}
						name="name"
						rules={[
							{
								required: true,
								message: t('__enter.please%single.name')
							}
						]}
					>
						<Input placeholder={t('__enter.please%single.name')} />
					</FormItem>
					<FormItem
						viewOnly={viewOnly}
						label={t('single.description')}
						name="description"
						rules={[
							{
								required: false,
								message: t('__enter.please%single.description')
							}
						]}
					>
						<TextArea
							rows={4}
							showCount
							placeholder={t('__enter.please%single.description')}
						/>
					</FormItem>

					{/* Conditionally render additional section */}
					{additionalEditSection &&
						additionalEditSection(viewOnly, form)}

					{!viewOnly && (
						<FormItem
							style={{ marginTop: '20px' }}
							wrapperCol={{
								xs: { span: 24 }, // Full width on mobile
								sm: { offset: 4, span: 20 } // Original layout for larger screens
							}}
						>
							<Button
								disabled={false}
								type="primary"
								htmlType="submit"
								style={{ width: '100%' }}
							>
								{item
									? t('single.update')
									: t('__add')}
							</Button>
						</FormItem>
					)}
				</Form>
			</Modal>
		)
	}
)

/*
* LstTypeInputBase is a generic component for displaying and editing a list of items.
* @param {UseLstClass} - The class to use for the list of items.
* @param {value} - The list of items to display and edit.
* @param {onChange} - The function to call when the list of items changes.
* @param {disabled} - Whether the component is disabled.

* - Additional props:
* @param {displayDescription} - Whether to display the description of each item.
* @param {phaseTwoLimited} - Whether to limit the status to phase two values.
* @param {additionalEditSection} - A function that returns additional edit form elements.
* @returns {JSX.Element} - The LstTypeInputBase component.

*/
const LstTypeInputBase = observer(
	({ UseLstClass = LstTypeBase, value, onChange, ...props }) => {
		const { disabled } = props

		const items = new UseLstClass(value || [])

		const [isModalVisible, setIsModalVisible] = useState(false)
		const [viewOnly, setViewOnly] = useState(false)
		const [editingItem, setEditingItem] = useState(null)
		/**
		 * showModal opens the EditItem modal in "add new item" mode.
		 *
		 * @function
		 * @private
		 */
		const showModal = () => {
			setEditingItem(null)
			setIsModalVisible(true)
			setViewOnly(false)
		}
		/**
		 * save writes the prepared list of items back via onChange().
		 *
		 * @function
		 * @private
		 */
		const save = () => {
			onChange(items.prepareForSave())
		}
		/**
		 * onSaveItem handles the submission from EditItem modal.
		 * It updates an existing item or adds a new one, then calls save() and closes the modal.
		 *
		 * @function
		 * @private
		 * @param {Object} values – The values returned by EditItem form.
		 */
		const onSaveItem = (values) => {
			if (editingItem) {
				items.updateItem(values)
			} else {
				items.addItem(values)
			}
			save()
			setIsModalVisible(false)
		}
		/**
		 * handleEdit loads an existing item into the EditItem modal for editing or view-only.
		 *
		 * @function
		 * @private
		 * @param {string} id – The ID of the item to edit.
		 */
		const handleEdit = (id) => {
			setViewOnly(false)
			const item = items.getItemById(id)
			setEditingItem(item)
			setIsModalVisible(true)
		}

		/**
		 * handleDelete removes an item by ID and updates the list via save().
		 *
		 * @function
		 * @private
		 * @param {string} id – The ID of the item to delete.
		 */
		const handleDelete = (id) => {
			items.deleteItem(id)
			save()
		}
		const { t } = useSmartTranslation()
		return (
			<>
				<List
					header={
						!disabled && (
							<Button type="dashed" onClick={showModal}>
								{t('__add')}
							</Button>
						)
					}
					bordered
					locale={{
						emptyText: t('single.no-data')
					}}
					dataSource={items.list}
					renderItem={(item) => (
						<List.Item
							actions={
								!disabled
									? [
											<Button
												key={item.id + 'edit'}
												type="link"
												onClick={() =>
													handleEdit(item.id)
												}
											>
												<EditOutlined />
											</Button>,
											<Button
												key={item.id + 'delete'}
												type="link"
												onClick={() =>
													handleDelete(item.id)
												}
											>
												<DeleteOutlined />
											</Button>
									  ]
									: []
							}
						>
							<Link
								onClick={() => {
									handleEdit(item.id)
									setViewOnly(true)
								}}
							>
								{item.name}{' '}
								{!props?.noProgress && (
									<span>- {item.status}</span>
								)}
							</Link>

							{props?.displayDescription && item?.description && (
								<div
									style={{
										fontSize: 12,
										color: '#7d7d7d',
										margin: '0 0 0 10px',
										padding: 0
									}}
								>
									{item.description}
								</div>
							)}
						</List.Item>
					)}
				/>
				{isModalVisible && (
					<EditItem
						viewOnly={viewOnly}
						item={editingItem}
						onSaveItem={onSaveItem}
						onCancel={() => setIsModalVisible(false)}
						phaseTwoLimited={props?.phaseTwoLimited}
						additionalEditSection={props?.additionalEditSection}
						noProgress={props?.noProgress}
					/>
				)}
			</>
		)
	}
)

export default LstTypeInputBase
